import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, ChevronLeft, CheckCircle2, AlertCircle, Clock,
  ClipboardList, X, Check, RefreshCw, User, LogOut, Home
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api';

// ─── Bouton profil discret (coin header) ─────────────────────────────────────
export function TerrainProfileButton() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setConfirming(false); } };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const initials = (user?.name || 'T').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div ref={ref} className="relative" data-testid="terrain-profile-btn">
      <button
        onClick={() => { setOpen(o => !o); setConfirming(false); }}
        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all"
        style={{ background: '#E5E7EB', color: '#374151', border: '2px solid transparent' }}>
        {initials}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 bg-white rounded-2xl shadow-xl border min-w-[220px] overflow-hidden"
          style={{ borderColor: '#E5E7EB' }}>
          {/* Infos utilisateur */}
          <div className="px-4 py-3 border-b" style={{ borderColor: '#F3F4F6' }}>
            <p className="text-sm font-semibold" style={{ color: '#111827' }}>{user?.name}</p>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{user?.email}</p>
          </div>

          {/* Actions */}
          <div className="py-1">
            <button onClick={() => { setOpen(false); navigate('/terrain'); }}
              data-testid="terrain-go-home-btn"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors"
              style={{ color: '#374151' }}>
              <Home size={15} /> Accueil terrain
            </button>

            {!confirming ? (
              <button onClick={() => setConfirming(true)}
                data-testid="terrain-logout-btn"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-red-50 transition-colors"
                style={{ color: '#DC2626' }}>
                <LogOut size={15} /> Se déconnecter
              </button>
            ) : (
              <div className="px-4 py-3 border-t" style={{ borderColor: '#FEE2E2', background: '#FEF2F2' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: '#DC2626' }}>
                  Vous devrez ressaisir les identifiants.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setConfirming(false)}
                    className="flex-1 py-1.5 rounded-lg text-xs font-semibold border"
                    style={{ borderColor: '#E5E7EB', color: '#6B7280' }}>
                    Annuler
                  </button>
                  <button onClick={handleLogout}
                    data-testid="terrain-logout-confirm-btn"
                    className="flex-1 py-1.5 rounded-lg text-xs font-bold text-white"
                    style={{ background: '#DC2626' }}>
                    Confirmer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Header commun terrain ────────────────────────────────────────────────────
function TerrainHeader({ title, subtitle, onBack, right }) {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 border-b bg-white"
      style={{ borderColor: '#E5E7EB' }}>
      {onBack && (
        <button onClick={onBack} data-testid="terrain-back-btn"
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0">
          <ChevronLeft size={22} style={{ color: '#374151' }} />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <h1 className="text-base font-bold truncate" style={{  color: '#111827' }}>
          {title}
        </h1>
        {subtitle && <p className="text-xs truncate" style={{ color: '#6B7280' }}>{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {right}
        <TerrainProfileButton />
      </div>
    </div>
  );
}

// ─── Liste des sessions en cours ──────────────────────────────────────────────
export function TerrainComptagesList() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getComptages()
      .then(r => setSessions((r.data || []).filter(s => s.statut === 'en_cours')))
      .catch(() => toast.error('Erreur chargement des sessions'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      <TerrainHeader
        title="Comptages en cours"
        subtitle="Sélectionnez une session à réaliser"
        right={
          <button onClick={() => { setLoading(true); api.getComptages().then(r => setSessions((r.data || []).filter(s => s.statut === 'en_cours'))).catch(() => {}).finally(() => setLoading(false)); }}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <RefreshCw size={18} style={{ color: '#6B7280' }} />
          </button>
        }
      />

      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-10 h-10 border-2 border-t-transparent rounded-full"
              style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ClipboardList size={48} className="mb-4" style={{ color: '#D1D5DB' }} />
            <p className="text-base font-semibold" style={{ color: '#6B7280' }}>Aucune session en cours</p>
            <p className="text-sm mt-1" style={{ color: '#9CA3AF' }}>
              Les sessions créées depuis le bureau apparaîtront ici
            </p>
          </div>
        ) : sessions.map(s => {
          const pct = s.nb_total > 0
            ? Math.round(((s.nb_present + s.nb_introuvable) / s.nb_total) * 100)
            : 0;
          return (
            <button key={s.comptage_id}
              onClick={() => navigate(`/terrain/comptages/${s.comptage_id}`)}
              data-testid={`terrain-session-${s.comptage_id}`}
              className="w-full bg-white rounded-2xl border p-5 text-left hover:shadow-md transition-all"
              style={{ borderColor: '#E5E7EB' }}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-bold truncate"
                    style={{  color: '#111827' }}>
                    {s.titre}
                  </h2>
                  {s.date_planifiee && (
                    <p className="text-sm mt-0.5" style={{ color: '#6B7280' }}>
                      Prévu le {new Date(s.date_planifiee).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  {s.plan_nom && (
                    <p className="text-xs mt-0.5" style={{ color: '#7C3AED' }}>{s.plan_nom}</p>
                  )}
                </div>
                <span className="flex-shrink-0 text-2xl font-bold"
                  style={{ color: 'var(--primary-solid)',  }}>
                  {pct}%
                </span>
              </div>
              <div className="flex items-center gap-5 text-sm mb-3">
                <span className="flex items-center gap-1.5 font-medium" style={{ color: '#16A34A' }}>
                  <CheckCircle2 size={15} />{s.nb_present} présents
                </span>
                <span className="flex items-center gap-1.5 font-medium" style={{ color: '#DC2626' }}>
                  <AlertCircle size={15} />{s.nb_introuvable} introuvables
                </span>
                <span className="flex items-center gap-1.5" style={{ color: '#6B7280' }}>
                  <Clock size={15} />{s.nb_en_attente} restants
                </span>
              </div>
              <div className="w-full h-2.5 rounded-full overflow-hidden flex" style={{ background: '#F3F4F6' }}>
                <div style={{ width: `${s.nb_total > 0 ? (s.nb_present / s.nb_total) * 100 : 0}%`, background: '#16A34A', transition: 'width 0.3s' }} />
                <div style={{ width: `${s.nb_total > 0 ? (s.nb_introuvable / s.nb_total) * 100 : 0}%`, background: '#DC2626', transition: 'width 0.3s' }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Détail d'une session en mode terrain ─────────────────────────────────────
export function TerrainComptageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comptage, setComptage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('en_attente');
  const [updating, setUpdating] = useState({});
  const [pendingNote, setPendingNote] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'present' | 'introuvable'
  const [noteText, setNoteText] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.getComptage(id);
      setComptage(r.data);
    } catch { toast.error('Erreur chargement de la session'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const marquer = async (materielId, statut, note = undefined) => {
    if (updating[materielId]) return;
    setUpdating(u => ({ ...u, [materielId]: true }));
    try {
      const r = await api.marquerStatutComptage(id, materielId, {
        statut_comptage: statut,
        ...(note !== undefined ? { commentaire: note } : {}),
      });
      setComptage(prev => ({
        ...prev,
        nb_present:     r.data.nb_present,
        nb_introuvable: r.data.nb_introuvable,
        nb_en_attente:  r.data.nb_en_attente,
        lignes: prev.lignes.map(l => l.materiel_id === materielId ? r.data.ligne : l),
      }));
    } catch { toast.error('Erreur mise à jour'); }
    finally { setUpdating(u => ({ ...u, [materielId]: false })); }
  };

  const handleAction = (materielId, action) => {
    if (pendingNote === materielId) { setPendingNote(null); setNoteText(''); setPendingAction(null); return; }
    setPendingNote(materielId);
    setPendingAction(action);
    setNoteText('');
  };

  const confirmAction = (materielId) => {
    marquer(materielId, pendingAction, noteText.trim() || null);
    setPendingNote(null);
    setNoteText('');
    setPendingAction(null);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
      <div className="animate-spin w-10 h-10 border-2 border-t-transparent rounded-full"
        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
    </div>
  );
  if (!comptage) return null;

  const isTerminee = comptage.statut === 'terminee';
  const pct = comptage.nb_total > 0
    ? Math.round(((comptage.nb_present + comptage.nb_introuvable) / comptage.nb_total) * 100)
    : 0;

  const lignesFiltrees = (comptage.lignes || []).filter(l => {
    const s = search.toLowerCase();
    const matchSearch = !s
      || (l.materiel_nom   || '').toLowerCase().includes(s)
      || (l.numero_parc    || '').toLowerCase().includes(s)
      || (l.numero_serie   || '').toLowerCase().includes(s)
      || (l.immatriculation || '').toLowerCase().includes(s);
    const matchStatut = !filterStatut || l.statut_comptage === filterStatut;
    return matchSearch && matchStatut;
  });

  const FILTERS = [
    { id: '',            label: 'Tous',        count: comptage.nb_total,       color: null },
    { id: 'en_attente',  label: 'En attente',  count: comptage.nb_en_attente,  color: '#6B7280' },
    { id: 'present',     label: 'Présent',     count: comptage.nb_present,     color: '#16A34A' },
    { id: 'introuvable', label: 'Introuvable', count: comptage.nb_introuvable, color: '#DC2626' },
  ];

  return (
    <div className="min-h-screen pb-8" style={{ background: '#F9FAFB' }}>
      {/* Header */}
      <TerrainHeader
        title={comptage.titre}
        subtitle={isTerminee ? 'Session clôturée' : `${comptage.nb_en_attente} matériels restants`}
        onBack={() => navigate('/terrain/comptages')}
        right={
          <div className="text-right">
            <p className="text-2xl font-bold leading-none"
              style={{  color: 'var(--primary-solid)' }}>{pct}%</p>
            <p className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>
              {comptage.nb_total - comptage.nb_en_attente}/{comptage.nb_total}
            </p>
          </div>
        }
      />

      {/* Barre de progression */}
      <div className="h-2 flex" style={{ background: '#E5E7EB' }}>
        <div style={{
          width: `${comptage.nb_total > 0 ? (comptage.nb_present / comptage.nb_total) * 100 : 0}%`,
          background: '#16A34A', transition: 'width 0.4s'
        }} />
        <div style={{
          width: `${comptage.nb_total > 0 ? (comptage.nb_introuvable / comptage.nb_total) * 100 : 0}%`,
          background: '#DC2626', transition: 'width 0.4s'
        }} />
      </div>

      {/* Barre de recherche */}
      <div className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="N° parc, N° série, immatriculation, nom..."
            data-testid="terrain-search"
            className="w-full pl-10 pr-10 py-3 rounded-xl border outline-none"
            style={{
              borderColor: '#D1D5DB', background: 'white',
              color: '#111827', fontSize: '16px'  /* évite zoom iOS */
            }}
          />
          {search && (
            <button onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1">
              <X size={16} style={{ color: '#9CA3AF' }} />
            </button>
          )}
        </div>
      </div>

      {/* Filtres rapides */}
      <div className="px-4 pb-3 flex gap-2 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {FILTERS.map(f => (
          <button key={f.id}
            onClick={() => setFilterStatut(f.id)}
            data-testid={`terrain-filter-${f.id || 'tous'}`}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition-all"
            style={{
              background: filterStatut === f.id ? (f.color || 'var(--primary-solid)') : 'white',
              color: filterStatut === f.id ? 'white' : (f.color || '#374151'),
              borderColor: filterStatut === f.id ? (f.color || 'var(--primary-solid)') : '#E5E7EB',
            }}>
            {f.label}
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
              style={{
                background: filterStatut === f.id ? 'rgba(255,255,255,0.25)' : '#F3F4F6',
                color: filterStatut === f.id ? 'white' : '#6B7280',
              }}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {/* Liste des cartes */}
      <div className="px-4 space-y-3" data-testid="terrain-cards-list">
        {lignesFiltrees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <ClipboardList size={40} className="mb-3" style={{ color: '#D1D5DB' }} />
            <p className="text-sm font-medium" style={{ color: '#6B7280' }}>
              {search ? 'Aucun résultat pour cette recherche' : 'Aucun matériel dans cette catégorie'}
            </p>
          </div>
        ) : lignesFiltrees.map(ligne => {
          const statut = ligne.statut_comptage;
          const isPresent     = statut === 'present';
          const isIntrouvable = statut === 'introuvable';
          const isPending     = pendingNote === ligne.materiel_id;
          const isBusy        = !!updating[ligne.materiel_id];

          return (
            <div key={ligne.materiel_id}
              data-testid={`terrain-card-${ligne.materiel_id}`}
              className="bg-white rounded-2xl border overflow-hidden transition-all"
              style={{
                borderColor: isPresent ? '#86EFAC' : isIntrouvable ? '#FECACA' : '#E5E7EB',
                boxShadow: isPresent ? '0 0 0 2px #F0FDF4' : isIntrouvable ? '0 0 0 2px #FEF2F2' : undefined,
              }}>

              {/* Infos matériel */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {ligne.numero_parc && (
                    <span className="font-mono text-lg font-bold" style={{ color: '#111827' }}>
                      {ligne.numero_parc}
                    </span>
                  )}
                  {isPresent     && <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#F0FDF4', color: '#16A34A', border: '1px solid #86EFAC' }}><Check size={10} />Présent</span>}
                  {isIntrouvable && <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}><AlertCircle size={10} />Introuvable</span>}
                  {statut === 'en_attente' && <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#F9FAFB', color: '#6B7280', border: '1px solid #E5E7EB' }}><Clock size={10} />En attente</span>}
                </div>

                <p className="text-base font-semibold leading-snug mb-1.5" style={{ color: '#111827' }}>
                  {ligne.materiel_nom}
                </p>

                {/* N° Série + Immat — toujours affichés, plus petits */}
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs" style={{ color: '#9CA3AF' }}>
                  {ligne.numero_serie && (
                    <span>N° Série : <span className="font-mono font-medium" style={{ color: '#6B7280' }}>{ligne.numero_serie}</span></span>
                  )}
                  {ligne.immatriculation && (
                    <span>Immat. : <span className="font-mono font-medium" style={{ color: '#6B7280' }}>{ligne.immatriculation}</span></span>
                  )}
                </div>

                {/* Note existante */}
                {ligne.commentaire && (
                  <p className="mt-2 text-sm italic px-2.5 py-1.5 rounded-lg"
                    style={{ background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' }}>
                    {ligne.commentaire}
                  </p>
                )}
              </div>

              {/* Zone saisie note (présent ou introuvable) */}
              {isPending && (
                <div className="px-4 pb-3 border-t"
                  style={{ borderColor: pendingAction === 'present' ? '#BBF7D0' : '#FEE2E2' }}>
                  <p className="text-xs font-semibold mb-2 pt-3"
                    style={{ color: pendingAction === 'present' ? '#16A34A' : '#DC2626' }}>
                    {pendingAction === 'present' ? 'Marquer comme présent — note optionnelle' : 'Marquer comme introuvable — note optionnelle'}
                  </p>
                  <div className="flex gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') confirmAction(ligne.materiel_id);
                        if (e.key === 'Escape') { setPendingNote(null); setNoteText(''); setPendingAction(null); }
                      }}
                      placeholder={pendingAction === 'present' ? 'Ex: Trouvé en zone A...' : 'Ex: Non trouvé en zone B...'}
                      data-testid={`terrain-note-input-${ligne.materiel_id}`}
                      className="flex-1 px-3 py-2.5 rounded-xl border outline-none"
                      style={{
                        borderColor: pendingAction === 'present' ? '#86EFAC' : '#FECACA',
                        background: pendingAction === 'present' ? '#F0FDF4' : '#FEF2F2',
                        color: '#111827', fontSize: '16px'
                      }}
                    />
                    <button onClick={() => confirmAction(ligne.materiel_id)}
                      data-testid={`terrain-note-confirm-${ligne.materiel_id}`}
                      className="px-4 py-2.5 rounded-xl text-white font-bold flex-shrink-0"
                      style={{ background: pendingAction === 'present' ? '#16A34A' : '#DC2626' }}>
                      <Check size={18} />
                    </button>
                    <button onClick={() => { setPendingNote(null); setNoteText(''); setPendingAction(null); }}
                      className="px-3 py-2.5 rounded-xl flex-shrink-0"
                      style={{ background: '#F3F4F6', color: '#6B7280' }}>
                      <X size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Boutons d'action — grands et tactiles */}
              {!isTerminee && !isPending && (
                <div className="flex border-t" style={{ borderColor: '#F3F4F6' }}>
                  <button
                    onClick={() => isPresent
                      ? marquer(ligne.materiel_id, 'en_attente')
                      : handleAction(ligne.materiel_id, 'present')}
                    disabled={isBusy}
                    data-testid={`terrain-present-${ligne.materiel_id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold border-r transition-colors disabled:opacity-40"
                    style={{
                      background: isPresent ? '#F0FDF4' : 'white',
                      color: isPresent ? '#16A34A' : '#9CA3AF',
                      borderColor: '#F3F4F6',
                      minHeight: '56px',
                    }}>
                    <CheckCircle2 size={22} />
                    Présent
                  </button>
                  <button
                    onClick={() => isIntrouvable
                      ? marquer(ligne.materiel_id, 'en_attente')
                      : handleAction(ligne.materiel_id, 'introuvable')}
                    disabled={isBusy}
                    data-testid={`terrain-introuvable-${ligne.materiel_id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold transition-colors disabled:opacity-40"
                    style={{
                      background: isIntrouvable ? '#FEF2F2' : 'white',
                      color: isIntrouvable ? '#DC2626' : '#9CA3AF',
                      minHeight: '56px',
                    }}>
                    <AlertCircle size={22} />
                    Introuvable
                  </button>
                </div>
              )}

              {isTerminee && (
                <div className="px-4 py-3 border-t text-center text-xs font-medium"
                  style={{ borderColor: '#F3F4F6', color: '#9CA3AF' }}>
                  Session clôturée — lecture seule
                </div>
              )}
            </div>
          );
        })}
      </div>

      {lignesFiltrees.length > 0 && (
        <p className="text-center text-xs mt-4 pb-2" style={{ color: '#9CA3AF' }}>
          {lignesFiltrees.length} matériel{lignesFiltrees.length !== 1 ? 's' : ''} affiché{lignesFiltrees.length !== 1 ? 's' : ''}
          {lignesFiltrees.length !== comptage.nb_total && ` sur ${comptage.nb_total}`}
        </p>
      )}
    </div>
  );
}
